import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getTeams } from '../../api/documentAPI';
import { isoLang } from '../../services/language';

// GetTeams -> položky výberu (IsMember = prihlásený je v tíme, MemberCount len pre správcu)
export const toTeamOptions = (response) => (response || []).map((team) => ({
    value: team.Id,
    label: team.Name,
    adDn: (team.AdDn ?? team.adDn ?? '').trim(),
    isMember: !!team.IsMember,
    memberCount: team.MemberCount
}));

// Kto priečinok uvidí (tímy priečinka = prístup v chate aj v Dokumentoch). Správca nemusí byť v tíme —
// vtedy priečinok sám neuvidí; len upozornenie, založenie sa neblokuje.
export const FolderAudience = ({ teams, selectedValues }) => {
    const [t, i18n] = useTranslation('global');
    if (!teams.length) return null;
    const selected = teams.filter((team) => selectedValues.includes(team.value));
    if (!selected.length) {
        return <div className="team-audience warn">{t('folderNoTeams')}</div>;
    }
    const lang = isoLang(i18n.language);
    const plural = new Intl.PluralRules(lang);
    const nf = new Intl.NumberFormat(lang);
    const names = selected.map((team) => team.memberCount == null ? team.label
        : `${team.label} (${t([`teamUsers_${plural.select(team.memberCount)}`, 'teamUsers_other'], { n: nf.format(team.memberCount) })})`);
    return (
        <>
            <div className="team-audience">{t('folderAudience', { teams: names.join(', ') })}</div>
            {!selected.some((team) => team.isMember) && (
                <div className="team-audience warn">{t('folderNotVisibleToYou')}</div>
            )}
        </>
    );
};

// teams (voliteľné): položky z toTeamOptions, ak ich už načítala rodičovská obrazovka
export default function TeamsMultiselect ({ selectedValues, onChange, teams: teamsProp }) {

    const dropdownRef = useRef(null);
    const [t] = useTranslation('global');
    const [loadedTeams, setLoadedTeams] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const teams = teamsProp || loadedTeams;

    useEffect(()=> {
        if (teamsProp) return;
        const fetchTeams = async () => {
            try {
              setLoadedTeams(toTeamOptions(await getTeams()));
            } catch (error) {
              console.error("Error fetching data: ", error);
            };
          }

        fetchTeams();
    },[]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false); // Close dropdown
            }
        };

        document.addEventListener('mousedown', handleClickOutside); // Listen for clicks
        return () => {
            document.removeEventListener('mousedown', handleClickOutside); // Cleanup on unmount
        };
    }, []);

    const handleSelect = (value) => {
        if (selectedValues.includes(value)) {
            // Remove value
            onChange({ target: { value: selectedValues.filter((v) => v !== value) } });
        } else {
            // Add value
            onChange({ target: { value: [...selectedValues, value] } });
        }
    };

    const handleDelete = (value) => {
        onChange({ target: { value: selectedValues.filter((v) => v !== value) } });
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const filteredTeams = teams.filter((team) =>
        team.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
        <div className="mill-multiselect" ref={dropdownRef}>
            <label>{t('teams')}</label>
            <div className="multiselect-line" onClick={toggleDropdown}>
                <div className="selected-values">
                    {selectedValues.map((value) => (
                        <span key={value} className="selected-chip team-selected-chip">
                            {teams.find((u) => u.value === value)?.label || value}
                            {teams.find((u) => u.value === value)?.adDn ? (
                                <span className="team-option-ad-badge">AD</span>
                            ) : null}
                            <button className="delete-chip" onClick={() => handleDelete(value)}>
                                &times;
                            </button>
                        </span>
                    ))}
                </div>
                <span className="dropdown-icon material-symbols-outlined">
                    {isDropdownOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                </span>
            </div>
            {isDropdownOpen && (
                <div className="dropdown-menu">
                    <div className='search-input-wrapper'>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={t('search')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <ul className="dropdown-list">
                        {filteredTeams.map((team) => (
                            <li key={team.value}
                                className={`dropdown-item team-dropdown-item ${
                                    selectedValues.includes(team.value) ? "selected" : ""
                                }`}
                                onClick={() => handleSelect(team.value)}
                            >
                                <span>{team.label}</span>
                                {team.adDn ? (
                                    <span className="team-option-ad-badge">AD</span>
                                ) : null}
                                {team.isMember ? (
                                    <span className="team-member-mark">{t('teamIsMember')}</span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
        <FolderAudience teams={teams} selectedValues={selectedValues} />
        </>
    );
}